import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { getShippingMethodForBuyNow } from '../helper/getShippingMethodForBuyNow';
import { type ProductDetailContext } from '../types/types';
import {
    LoadingPayPalEligibilityStatus,
    usePayPalExpressEligibility,
} from './usePayPalExpressEligibility';
import { usePayPalExpressError } from './usePayPalExpressError';
import { usePrice } from './usePrice';
import { useProduct } from './useProduct';

type UseBuyboxPayPalCheckoutParams = {
    readonly isCheckoutDisabled: boolean;
};

type UseBuyboxPayPalCheckoutReturn = {
    readonly handlePayPalError: ReturnType<typeof usePayPalExpressError>;
    readonly quantity: number;
    readonly showPayPalCheckout: boolean;
};

function useBuyboxPayPalCheckout({
    isCheckoutDisabled,
}: UseBuyboxPayPalCheckoutParams): UseBuyboxPayPalCheckoutReturn {
    const product = useProduct();
    const { price } = usePrice();
    const {
        paymentServiceProviderConfig: { paypalClientScriptUrl },
    } = useFragmentContext<ProductDetailContext>();

    const isPaypalScriptAvailable = Boolean(paypalClientScriptUrl);

    const quantity = Number(price.multiplier || 1);
    const pricePerUnit = price.gross.amount;
    const estimatedTotal = Number(pricePerUnit) * quantity;
    const shippingMethod = getShippingMethodForBuyNow(product);
    const {
        isPayPalEligible,
        loading: payPalEligibilityLoading,
        wasEverEligible,
    } = usePayPalExpressEligibility(isPaypalScriptAvailable ? estimatedTotal : 0, shippingMethod);
    const handlePayPalError = usePayPalExpressError();

    const isPayPalCheckoutEligible =
        payPalEligibilityLoading === LoadingPayPalEligibilityStatus.Done
            ? isPayPalEligible
            : wasEverEligible;

    const showPayPalCheckout =
        isPaypalScriptAvailable && isPayPalCheckoutEligible && !isCheckoutDisabled;

    return {
        handlePayPalError,
        quantity,
        showPayPalCheckout,
    };
}

export { useBuyboxPayPalCheckout };
